import {
  GA4EventName,
  GA4EventParamName,
  trackPosterBuilderGA4Events,
} from '@Libraries/ga-events';
import type {
  GA4EventSources,
  TimelineEditAction,
  TimelineMultiSelectType,
  TimelineMuteTarget,
  TimelineOpenSource,
  TimelineSplitSource,
  TimelineZoomSource,
} from '@Libraries/ga-events';
import {CanvasZoomSource, TimelineState, ZoomDirection} from '@Libraries/ga-events';
import {isEditorMobileVariant} from '@Libraries/is-editor-mobile-variant';
import type {ItemAddType} from '@PosterWhiteboard/page/page.types';
import type {CropMediaType} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';

export const GA4_ITEM_TYPE_MIXED_SELECTION = 'mixed';

const MILLISECONDS_PER_SECOND = 1000;
const MUTE_TOGGLE_MUTED_VALUE = 'mute';
const MUTE_TOGGLE_UNMUTED_VALUE = 'unmute';
const WHEEL_ZOOM_GESTURE_IDLE_MS = 400;
const HOTJAR_EVENT_CROP_CLICKED = 'CropClicked';

let timelineOpenedAtMs: number | null = null;
let hasTrackedTimelineMultiSelect = false;

const getEditorVariantParam = (): string => {
  return isEditorMobileVariant() ? 'mobile' : 'web';
};

const isTimelineOpen = (): boolean => {
  return window.PMW.redux.store.getState().posterEditor.isTimelineModeOn ?? false;
};

/** For open triggers that also fire while the timeline is already showing (audio auto-reveal, panel adds) — an already-open timeline must not report a second open. */
export const trackTimelineOpenIfClosed = (source: TimelineOpenSource): void => {
  if (isTimelineOpen()) {
    return;
  }
  trackTimelineOpen(source);
};

export const trackTimelineOpen = (source: TimelineOpenSource): void => {
  timelineOpenedAtMs = Date.now();
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_OPEN, {
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
    [GA4EventParamName.SOURCE]: source,
  });
};

/** Close counterpart of `trackTimelineOpenIfClosed`: every close path funnels through `Poster.updateIsTimelineMode(false)`, including ones reached while the timeline is already hidden (page turned non-video on load, repeated resets) — only an actually-open timeline reports a close. */
export const trackTimelineCloseIfOpen = (): void => {
  if (!isTimelineOpen()) {
    return;
  }
  trackTimelineClose();
};

export const trackTimelineClose = (): void => {
  const params: Record<string, string> = {
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  };
  if (timelineOpenedAtMs !== null) {
    params[GA4EventParamName.VALUE] = `${Math.round((Date.now() - timelineOpenedAtMs) / MILLISECONDS_PER_SECOND)}`;
    timelineOpenedAtMs = null;
  }
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_CLOSE, params);
};

export const trackTimelineMediaAdded = (mediaType: string, option: string): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_MEDIA_ADDED, {
    [GA4EventParamName.TYPE]: mediaType,
    [GA4EventParamName.VALUE]: option,
  });
};

export const trackTimelineMuteToggled = (target: TimelineMuteTarget, isNowMuted: boolean): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_MUTE_TOGGLED, {
    [GA4EventParamName.TYPE]: target,
    [GA4EventParamName.VALUE]: isNowMuted ? MUTE_TOGGLE_MUTED_VALUE : MUTE_TOGGLE_UNMUTED_VALUE,
  });
};

export const trackTimelineEditCommitted = (action: TimelineEditAction, itemType: string): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_EDIT_COMMITTED, {
    [GA4EventParamName.ACTION]: action,
    [GA4EventParamName.ITEM_TYPE]: itemType,
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  });
};

export const trackTimelineSplit = (source: TimelineSplitSource, itemType: string): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_SPLIT, {
    [GA4EventParamName.SOURCE]: source,
    [GA4EventParamName.ITEM_TYPE]: itemType,
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  });
};

export const getZoomDirection = (fromScale: number, toScale: number): ZoomDirection | null => {
  if (toScale > fromScale) {
    return ZoomDirection.IN;
  }
  if (toScale < fromScale) {
    return ZoomDirection.OUT;
  }
  return null;
};

export const trackTimelineZoom = (source: TimelineZoomSource, direction: ZoomDirection): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_ZOOM, {
    [GA4EventParamName.SOURCE]: source,
    [GA4EventParamName.DIRECTION]: direction,
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  });
};

export const trackCanvasZoom = (source: CanvasZoomSource, direction: ZoomDirection): void => {
  trackPosterBuilderGA4Events(GA4EventName.CANVAS_ZOOM, {
    [GA4EventParamName.SOURCE]: source,
    [GA4EventParamName.DIRECTION]: direction,
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  });
};

export interface WheelZoomGestureTracker {
  onWheelZoom(scaleBeforeEvent: number, scaleAfterEvent: number): void;
  cancel(): void;
}

/**
 * Wheel scrolling and trackpad pinching arrive as a burst of wheel events with no end signal, so a
 * gesture is the burst up to its first idle gap. Reports the burst's net direction once, when it ends.
 */
export const createWheelZoomGestureTracker = (onGestureEnd: (direction: ZoomDirection) => void): WheelZoomGestureTracker => {
  let gestureStartScale: number | null = null;
  let gestureEndScale = 0;
  let idleTimer: number | undefined;

  const finishGesture = (): void => {
    const direction = gestureStartScale === null ? null : getZoomDirection(gestureStartScale, gestureEndScale);
    gestureStartScale = null;
    idleTimer = undefined;
    if (direction) {
      onGestureEnd(direction);
    }
  };

  return {
    onWheelZoom: (scaleBeforeEvent: number, scaleAfterEvent: number): void => {
      gestureStartScale ??= scaleBeforeEvent;
      gestureEndScale = scaleAfterEvent;
      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(finishGesture, WHEEL_ZOOM_GESTURE_IDLE_MS);
    },
    cancel: (): void => {
      window.clearTimeout(idleTimer);
      gestureStartScale = null;
      idleTimer = undefined;
    },
  };
};

/** Fit-to-screen only counts as a zoom when it actually changes the scale. */
export const trackCanvasFitToScreen = (currentScale: number, fitToScreenScale: number): void => {
  const direction = getZoomDirection(currentScale, fitToScreenScale);
  if (direction) {
    trackCanvasZoom(CanvasZoomSource.FIT_TO_SCREEN, direction);
  }
};

export const trackTimelineMultiSelectOnceIfNeeded = (type: TimelineMultiSelectType): void => {
  if (hasTrackedTimelineMultiSelect) {
    return;
  }
  hasTrackedTimelineMultiSelect = true;
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_MULTISELECT_USED, {
    [GA4EventParamName.TYPE]: type,
  });
};

export const trackTimelineHeightResized = (heightPx: number): void => {
  trackPosterBuilderGA4Events(GA4EventName.TIMELINE_HEIGHT_RESIZED, {
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
    [GA4EventParamName.VALUE]: `${Math.round(heightPx)}`,
  });
};

export const trackEditorItemDeleted = (source: GA4EventSources, itemType: string): void => {
  trackPosterBuilderGA4Events(GA4EventName.EDITOR_ITEM_DELETED, {
    [GA4EventParamName.SOURCE]: source,
    [GA4EventParamName.ITEM_TYPE]: itemType,
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  });
};

export const trackEditorItemAdded = (itemType: string, addType: ItemAddType): void => {
  trackPosterBuilderGA4Events(GA4EventName.EDITOR_ITEM_ADDED, {
    [GA4EventParamName.ITEM_TYPE]: itemType,
    [GA4EventParamName.ADD_TYPE]: addType,
    [GA4EventParamName.TIMELINE_STATE]: isTimelineOpen() ? TimelineState.OPEN : TimelineState.CLOSED,
    [GA4EventParamName.TYPE]: getEditorVariantParam(),
  });
};

/**
 * Every way into crop mode (side panel, bottom bar, double click/tap, the C key, backgrounds) funnels through
 * `PosterMediaCrop.enterCropMode`, which reports it here once. Hotjar's crop trigger was set up as `CropClicked`,
 * so it is sent under that name alongside the GA4 event.
 */
export const trackCropClicked = (mediaType: CropMediaType): void => {
  trackPosterBuilderGA4Events(GA4EventName.CROP_ELEMENT, {[GA4EventParamName.TYPE]: mediaType});
  window.PMW.gtm.trackHotjarEvent(HOTJAR_EVENT_CROP_CLICKED);
};
